/**
 * AudiobookListPage - Main audiobook management page
 *
 * Orchestrates audiobook list view with split panel detail view
 */
package com.castly.admin.ui.audiobooks

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.AudiobookImportForm
import com.castly.admin.ui.ReaderSyncForm
import com.castly.admin.ui.BulkEpisodeImportForm
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.AudiobookDto
import com.castly.shared.dto.CreateAudiobookRequest
import com.castly.shared.dto.LabelDto
import com.castly.shared.dto.UpdateAudiobookRequest
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun AudiobookListPage(
    apiClient: ApiClient,
    onAudiobookClick: (String) -> Unit = {},
    onEditAudiobook: (String) -> Unit = {}
) {
    val colors = AppTheme.colors
    var audiobooks by remember { mutableStateOf<List<AudiobookDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var totalCount by remember { mutableStateOf(0) }
    var currentPage by remember { mutableStateOf(1) }
    var searchQuery by remember { mutableStateOf("") }
    var selectedTags by remember { mutableStateOf(mutableSetOf<String>()) }
    var tags by remember { mutableStateOf<List<com.castly.shared.dto.TagWithCountDto>>(emptyList()) }
    var selectedAudiobook by remember { mutableStateOf<AudiobookDto?>(null) }
    var activeTab by remember { mutableStateOf(AudiobookDetailTab.DATA) }
    var showImportDialog by remember { mutableStateOf(false) }
    var showSyncReadersDialog by remember { mutableStateOf(false) }
    var showBulkEpisodeImportDialog by remember { mutableStateOf(false) }
    var showOnixImportDialog by remember { mutableStateOf(false) }
    var showIsbnLookupDialog by remember { mutableStateOf(false) }
    var showRssImportDialog by remember { mutableStateOf(false) }
    var showCreateDialog by remember { mutableStateOf(false) }
    var createError by remember { mutableStateOf<String?>(null) }
    var isCreating by remember { mutableStateOf(false) }
    var showEditDialog by remember { mutableStateOf(false) }
    var editingAudiobookId by remember { mutableStateOf<String?>(null) }
    var editError by remember { mutableStateOf<String?>(null) }
    var isEditing by remember { mutableStateOf(false) }
    var showDeleted by remember { mutableStateOf(false) }
    var selectedLabelId by remember { mutableStateOf<Int?>(null) }
    var labels by remember { mutableStateOf<List<LabelDto>>(emptyList()) }
    val isAdmin = remember {
        window.localStorage.getItem("user_role")?.uppercase() in setOf("ADMIN", "SUPER_ADMIN") ||
        window.localStorage.getItem("is_label_admin") == "true"
    }

    val pageSize = 20
    val scope = rememberCoroutineScope()

    // Load labels for filter dropdown (admins only)
    LaunchedEffect(Unit) {
        if (isAdmin) {
            try { labels = apiClient.listLabels(includeInactive = true) } catch (_: Exception) {}
        }
    }

    // Load tags — counts are label-scoped server-side, so they must be reloaded whenever
    // the selected label changes or the chips would show another label's numbers.
    fun loadTags() {
        scope.launch {
            try {
                tags = apiClient.listAudiobookTags(labelId = selectedLabelId)
            } catch (e: Exception) {
                console.log("Failed to load tags: ${e.message}")
            }
        }
    }

    fun loadAudiobooks() {
        scope.launch {
            loading = true
            error = null
            try {
                val response = apiClient.listAudiobooks(
                    page = currentPage,
                    pageSize = pageSize,
                    query = searchQuery.takeIf { it.isNotBlank() },
                    tagIds = selectedTags.takeIf { it.isNotEmpty() },
                    includeDeleted = showDeleted,
                    labelId = selectedLabelId
                )
                audiobooks = response.audiobooks
                totalCount = response.total
            } catch (e: Exception) {
                error = e.message ?: "Failed to load audiobooks"
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(selectedLabelId) {
        loadTags()
    }

    LaunchedEffect(currentPage, searchQuery, selectedTags, showDeleted, selectedLabelId) {
        loadAudiobooks()
    }

    Div({
        style {
            height(100.vh)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Color(colors.surfaceHover))
            padding(Spacing.lg)
            themeTransition()
        }
    }) {
        AudiobooksSplitView(
            audiobooks = audiobooks,
            loading = loading,
            error = error,
            totalCount = totalCount,
            currentPage = currentPage,
            searchQuery = searchQuery,
            tags = tags,
            selectedTags = selectedTags,
            selectedAudiobook = selectedAudiobook,
            activeTab = activeTab,
            apiClient = apiClient,
            pageSize = pageSize,
            onSearchChange = { query ->
                searchQuery = query
                currentPage = 1
            },
            onFilterTagsChange = { newTags ->
                selectedTags = newTags
                currentPage = 1
            },
            onPageChange = { page ->
                currentPage = page
            },
            onImportCsv = {
                showImportDialog = true
            },
            onSyncReaders = {
                showSyncReadersDialog = true
            },
            onImportEpisodes = {
                showBulkEpisodeImportDialog = true
            },
            onImportOnix = {
                showOnixImportDialog = true
            },
            onIsbnLookup = {
                showIsbnLookupDialog = true
            },
            onImportRss = {
                showRssImportDialog = true
            },
            onCreateAudiobook = {
                showCreateDialog = true
                createError = null
            },
            onRecalculateDurations = {
                scope.launch {
                    try {
                        apiClient.recalculateDurations()
                        loadAudiobooks()
                    } catch (e: Exception) {
                        error = "Failed to recalculate durations: ${e.message}"
                    }
                }
            },
            onAudiobookClick = { audiobook ->
                selectedAudiobook = audiobook
                activeTab = AudiobookDetailTab.DATA
            },
            onTabChange = { tab ->
                activeTab = tab
            },
            onAudiobookUpdate = { audiobookId ->
                scope.launch {
                    try {
                        val updated = apiClient.getAudiobook(audiobookId)
                        selectedAudiobook = updated
                        loadAudiobooks()
                        loadTags() // Refresh tags to update counts
                    } catch (e: Exception) {
                        error = "Failed to refresh audiobook: ${e.message}"
                    }
                }
            },
            onClose = {
                selectedAudiobook = null
            },
            onRefresh = {
                loadAudiobooks()
                loadTags()
            },
            showDeleted = showDeleted,
            onToggleShowDeleted = { showDeleted = !showDeleted },
            onEditAudiobook = { audiobookId ->
                editingAudiobookId = audiobookId
                editError = null
                showEditDialog = true
            }
        )
    }

    // Import CSV dialog modal
    if (showImportDialog) {
        Div({
            style {
                position(Position.Fixed)
                top(0.px)
                left(0.px)
                width(100.percent)
                height(100.percent)
                backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                property("z-index", "1000")
            }
        }) {
            Div({
                style {
                    maxWidth(600.px)
                    width(90.percent)
                    maxHeight(90.percent)
                    overflowY("auto")
                }
            }) {
                AudiobookImportForm(
                    apiClient = apiClient,
                    labels = labels,
                    userLabelId = window.localStorage.getItem("user_label_id")?.toIntOrNull(),
                    isLabelEditor = window.localStorage.getItem("user_role")?.uppercase() == "LABEL_EDITOR",
                    onImportComplete = { report ->
                        console.log("CSV import completed", report)
                        // Reload audiobooks list
                        loadAudiobooks()
                    },
                    onCancel = {
                        showImportDialog = false
                    }
                )
            }
        }
    }

    // Sync Readers dialog modal
    if (showSyncReadersDialog) {
        Div({
            style {
                position(Position.Fixed)
                top(0.px)
                left(0.px)
                width(100.percent)
                height(100.percent)
                backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                property("z-index", "1000")
            }
        }) {
            Div({
                style {
                    maxWidth(600.px)
                    width(90.percent)
                    maxHeight(90.percent)
                    overflowY("auto")
                }
            }) {
                ReaderSyncForm(
                    apiClient = apiClient,
                    onSyncComplete = { report ->
                        console.log("Reader sync completed: ${report.syncedCount} readers synced")
                        // Reload audiobooks list to see updated reader assignments
                        loadAudiobooks()
                    },
                    onCancel = {
                        showSyncReadersDialog = false
                    }
                )
            }
        }
    }

    // ONIX Import dialog modal
    if (showOnixImportDialog) {
        Div({
            style {
                position(Position.Fixed)
                top(0.px)
                left(0.px)
                width(100.percent)
                height(100.percent)
                backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                property("z-index", "1000")
            }
        }) {
            Div({
                style {
                    maxWidth(800.px)
                    width(90.percent)
                    maxHeight(90.percent)
                    overflowY("auto")
                }
            }) {
                OnixImportForm(
                    apiClient = apiClient,
                    labels = labels,
                    userLabelId = window.localStorage.getItem("user_label_id")?.toIntOrNull(),
                    isLabelEditor = window.localStorage.getItem("user_role")?.uppercase() == "LABEL_EDITOR",
                    onImportComplete = {
                        loadAudiobooks()
                        loadTags()
                    },
                    onCancel = {
                        showOnixImportDialog = false
                    }
                )
            }
        }
    }

    // ISBN Lookup dialog modal
    if (showIsbnLookupDialog) {
        Div({
            style {
                position(Position.Fixed)
                top(0.px)
                left(0.px)
                width(100.percent)
                height(100.percent)
                backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                property("z-index", "1000")
            }
        }) {
            Div({
                style {
                    maxWidth(600.px)
                    width(90.percent)
                    maxHeight(90.percent)
                    overflowY("auto")
                }
            }) {
                IsbnLookupForm(
                    apiClient = apiClient,
                    onCancel = {
                        showIsbnLookupDialog = false
                    }
                )
            }
        }
    }

    // RSS Import dialog modal
    if (showRssImportDialog) {
        Div({
            style {
                position(Position.Fixed)
                top(0.px)
                left(0.px)
                width(100.percent)
                height(100.percent)
                backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                property("z-index", "1000")
            }
        }) {
            Div({
                style {
                    maxWidth(700.px)
                    width(90.percent)
                    maxHeight(90.percent)
                    overflowY("auto")
                }
            }) {
                RssAudiobookImportForm(
                    apiClient = apiClient,
                    labels = labels,
                    userLabelId = window.localStorage.getItem("user_label_id")?.toIntOrNull(),
                    isLabelEditor = window.localStorage.getItem("user_role")?.uppercase() == "LABEL_EDITOR",
                    onImportComplete = { audiobookId ->
                        console.log("RSS import completed: $audiobookId")
                        loadAudiobooks()
                        loadTags()
                    },
                    onCancel = {
                        showRssImportDialog = false
                    }
                )
            }
        }
    }

    // Create Audiobook dialog modal
    if (showCreateDialog) {
        var createAuthors by remember { mutableStateOf<List<com.castly.shared.dto.AuthorDto>>(emptyList()) }
        var createReaders by remember { mutableStateOf<List<com.castly.shared.dto.ReaderDto>>(emptyList()) }

        LaunchedEffect(showCreateDialog) {
            try {
                createAuthors = apiClient.listAuthors(pageSize = 500).authors
                createReaders = apiClient.listReaders(pageSize = 500).readers
            } catch (e: Exception) {
                console.log("Failed to load form data: ${e.message}")
            }
        }

        Div({
            style {
                position(Position.Fixed)
                top(0.px)
                left(0.px)
                width(100.percent)
                height(100.percent)
                backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                property("z-index", "1000")
            }
        }) {
            Div({
                style {
                    maxWidth(700.px)
                    width(90.percent)
                    maxHeight(90.percent)
                    overflowY("auto")
                }
            }) {
                AudiobookForm(
                    authors = createAuthors,
                    readers = createReaders,
                    labels = labels,
                    userLabelId = window.localStorage.getItem("user_label_id")?.toIntOrNull(),
                    isLabelEditor = window.localStorage.getItem("user_role")?.uppercase() == "LABEL_EDITOR",
                    isLoading = isCreating,
                    errorMessage = createError,
                    onSave = { formData ->
                        scope.launch {
                            isCreating = true
                            createError = null
                            try {
                                val request = CreateAudiobookRequest(
                                    isbn = formData.isbn.takeIf { it.isNotBlank() },
                                    title = formData.title,
                                    subtitle = formData.subtitle,
                                    titleEnglish = formData.titleEnglish,
                                    subtitleEnglish = formData.subtitleEnglish,
                                    authorId = formData.authorId?.takeIf { it > 0 },
                                    readerId = formData.readerId,
                                    publisher = formData.publisher,
                                    language = formData.language,
                                    description = formData.description,
                                    copyrightYear = formData.copyrightYear,
                                    releaseDate = formData.releaseDate,
                                    labelId = formData.labelId
                                )
                                val created = apiClient.createAudiobook(request)
                                showCreateDialog = false
                                loadAudiobooks()
                                loadTags()
                                selectedAudiobook = created
                                activeTab = AudiobookDetailTab.DATA
                            } catch (e: Exception) {
                                createError = e.message ?: "Failed to create audiobook"
                            } finally {
                                isCreating = false
                            }
                        }
                    },
                    onCancel = {
                        showCreateDialog = false
                    }
                )
            }
        }
    }

    // Edit Audiobook dialog modal
    if (showEditDialog && editingAudiobookId != null) {
        val editAudiobook = selectedAudiobook?.takeIf { it.id == editingAudiobookId }
        var editAuthors by remember { mutableStateOf<List<com.castly.shared.dto.AuthorDto>>(emptyList()) }
        var editReaders by remember { mutableStateOf<List<com.castly.shared.dto.ReaderDto>>(emptyList()) }

        LaunchedEffect(showEditDialog) {
            try {
                editAuthors = apiClient.listAuthors(pageSize = 500).authors
                editReaders = apiClient.listReaders(pageSize = 500).readers
            } catch (e: Exception) {
                console.log("Failed to load form data: ${e.message}")
            }
        }

        if (editAudiobook != null) {
            Div({
                style {
                    position(Position.Fixed)
                    top(0.px)
                    left(0.px)
                    width(100.percent)
                    height(100.percent)
                    backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.Center)
                    property("z-index", "1000")
                }
            }) {
                Div({
                    style {
                        maxWidth(700.px)
                        width(90.percent)
                        maxHeight(90.percent)
                        overflowY("auto")
                    }
                }) {
                    AudiobookForm(
                        audiobook = editAudiobook,
                        authors = editAuthors,
                        readers = editReaders,
                        isLoading = isEditing,
                        errorMessage = editError,
                        onSave = { formData ->
                            scope.launch {
                                isEditing = true
                                editError = null
                                try {
                                    val request = UpdateAudiobookRequest(
                                        isbn = formData.isbn.takeIf { it.isNotBlank() },
                                        title = formData.title,
                                        subtitle = formData.subtitle,
                                        titleEnglish = formData.titleEnglish,
                                        subtitleEnglish = formData.subtitleEnglish,
                                        authorId = formData.authorId?.takeIf { it > 0 },
                                        readerId = formData.readerId,
                                        publisher = formData.publisher,
                                        language = formData.language,
                                        description = formData.description,
                                        copyrightYear = formData.copyrightYear,
                                        releaseDate = formData.releaseDate
                                    )
                                    val updated = apiClient.updateAudiobook(editingAudiobookId!!, request)
                                    showEditDialog = false
                                    editingAudiobookId = null
                                    selectedAudiobook = updated
                                    loadAudiobooks()
                                    loadTags()
                                } catch (e: Exception) {
                                    editError = e.message ?: "Failed to update audiobook"
                                } finally {
                                    isEditing = false
                                }
                            }
                        },
                        onCancel = {
                            showEditDialog = false
                            editingAudiobookId = null
                        }
                    )
                }
            }
        }
    }

    // Bulk Episode Import dialog modal
    if (showBulkEpisodeImportDialog) {
        Div({
            style {
                position(Position.Fixed)
                top(0.px)
                left(0.px)
                width(100.percent)
                height(100.percent)
                backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                property("z-index", "1000")
            }
        }) {
            Div({
                style {
                    maxWidth(700.px)
                    width(90.percent)
                    maxHeight(90.percent)
                    overflowY("auto")
                }
            }) {
                BulkEpisodeImportForm(
                    apiClient = apiClient,
                    onImportComplete = { report ->
                        console.log("Bulk episode import completed: ${report.status}")
                        // Reload audiobooks list to reflect updated episode counts
                        loadAudiobooks()
                        loadTags()
                    },
                    onCancel = {
                        showBulkEpisodeImportDialog = false
                    }
                )
            }
        }
    }
}
